import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Grid } from '@material-ui/core';
import { navigate } from '@gatsbyjs/reach-router/';
import { useGlobal, Typography } from '@rcg/ui-react';
import CheckIcon from '@material-ui/icons/CheckCircle';
import { useReactiveVar, useQuery } from '@apollo/client';
import { cookieCountry } from '../../utils/globalization/marketCookies';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import dict from '../../utils/dictionary';
import Button from '../../components/UiComponents/Button';
import useStyles from './index.style';
import useDictionary from '../../hooks/useDictionary';
import {
  NAVIGATION_PATHS,
  TRAVEL_AGENT_RESERVE,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  SESSION_STORAGE_CURRENT_BOOKING,
  SESSION_STORAGE_IS_REFUND,
  SESSION_STORAGE_LAST_SEARCH_PARAMS,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  FLIGHT_TYPE_CODES,
  SUPPORT_PHONE_NUMBER_LD_FLAG,
  APPLICATION_NAME,
  BOOKING_COUNTRY,
  GROUP_BOOKING,
} from '../../utils/constants';
import { getManageRedirectionUrl } from '../../utils/common';

import {
  bookingProfileVar,
  bookedReservationVar,
  paymentVar,
  selectedDepartureVar,
  selectedReturnVar,
  heldReservationVar,
  giftCardPaymentVar,
} from '../../operations/localState/reactiveVariables';
import { LAUNCH_DARKLY_FLAGS_AIR } from '../../operations/queries';
import ReviewCard from '../../components/ReviewCard';
import TravelPlansUnified from '../../components/TravelPlansUnified';
import { trackConfirmationView } from '../../utils/analytics/searchTracking';
import { trackSuccesGiftCardPayment } from '../../utils/analytics/browseTracking';
import { trackInteraction } from '../../utils/analytics/bookFlightsTracking';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '../../operations/sessionStorage/utils';
import NotificationBannerComponent from '../../components/NotificationBanner';
import useCommonStyles from '../../utils/useCommonStyles';
import getBrand from '../../utils/getBrand';
import { LDUser } from '../../utils/ldUser';

const Confirmation = () => {
  const classes = useStyles();
  const styles = useCommonStyles();
  const {
    data: { lang, currency, country: globalCountry, market, localizedDayjs },
  } = useGlobal();
  const getDictionaryValue = useDictionary(dict);
  const [expanded, setExpanded] = useState(true);
  const [showSuccessBanner, setShowSuccessBanner] = useState(true);
  const [showTransferBanner, setShowTransferBanner] = useState(true);

  const currentBooking = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
  const isRefund = getSessionStorageItem(SESSION_STORAGE_IS_REFUND) || false;
  const transfersDetails = currentBooking?.flightProfile.guests[0].flightGuest.transfers;

  useEffect(() => {
    document.querySelector('.main-container').scrollTo(0, 0);
    setSessionStorageItem(SESSION_STORAGE_LAST_SEARCH_PARAMS, null);
  }, []);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const selectedReturn = useReactiveVar(selectedReturnVar);
  const bookingProfile = useReactiveVar(bookingProfileVar);
  const bookedReservationConfirmation = useReactiveVar(bookedReservationVar);
  const heldReservationPayment = useReactiveVar(heldReservationVar);
  const paymentTrakerInfo = useReactiveVar(giftCardPaymentVar);
  const selectedDeparture = useReactiveVar(selectedDepartureVar);
  const payment = useReactiveVar(paymentVar);
  const showTAReserveBanner = getSessionStorageItem(TRAVEL_AGENT_RESERVE);
  const isChangeFlights = getSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS);
  const flightTypeCodeFromSession = getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE);
  const isOneWay = flightTypeCodeFromSession === FLIGHT_TYPE_CODES.ONE_WAY;

  const isGroupBooking = bookingProfile?.currentBooking?.type === GROUP_BOOKING;

  const departingConfirmNumbers =
    bookedReservationConfirmation?.bookFlight?.outboundFlight?.confirmationNumbers || null;
  const returningConfirmNumbers =
    bookedReservationConfirmation?.bookFlight?.inboundFlight?.confirmationNumbers || null;

  const departConfNumListDisplay =
    departingConfirmNumbers && Array.from(departingConfirmNumbers).join(', ');
  const returnConfNumListDisplay =
    returningConfirmNumbers && Array.from(returningConfirmNumbers).join(', ');

  const sameNumberForDepartingAndReturning = departingConfirmNumbers?.every((departConNum) =>
    returningConfirmNumbers?.includes(departConNum),
  );

  const payNowSelected = payment?.method === 'payNow';
  const { holdFlight } = heldReservationPayment;
  let dataFlight = bookedReservationConfirmation.bookFlight;
  if (dataFlight) {
    dataFlight = { ...dataFlight, priceChange: holdFlight.priceChange };
  }

  const bookCountryFromSession = getSessionStorageItem(BOOKING_COUNTRY) || {
    country: 'USA',
    officeID: 'mia',
  };

  const ldCallVariables = {
    application: APPLICATION_NAME,
    brand: getBrand().ldBrand,
    bookingCountry: bookCountryFromSession.country,
    clientApp: APPLICATION_NAME,
    country: cookieCountry().toUpperCase(),
    ldUser: LDUser(),
    language: lang,
  };

  const { data: ldFlagsData } = useQuery(LAUNCH_DARKLY_FLAGS_AIR, {
    variables: ldCallVariables,
  });

  const airSupportPhoneNumber = ldFlagsData?.ldFlags[SUPPORT_PHONE_NUMBER_LD_FLAG];

  useEffect(() => {
    if (paymentTrakerInfo) {
      trackSuccesGiftCardPayment(paymentTrakerInfo);
    }
  }, [paymentTrakerInfo]);

  useEffect(() => {
    trackConfirmationView(
      payNowSelected,
      sameNumberForDepartingAndReturning
        ? departConfNumListDisplay
        : `${departConfNumListDisplay}-${returnConfNumListDisplay}`,
    );
  }, [
    payNowSelected,
    sameNumberForDepartingAndReturning,
    departConfNumListDisplay,
    returnConfNumListDisplay,
  ]);

  if (
    !bookedReservationConfirmation ||
    !bookedReservationConfirmation.bookFlight ||
    (!bookedReservationConfirmation.bookFlight.inboundFlight &&
      !bookedReservationConfirmation.bookFlight.outboundFlight)
  ) {
    window.location.href = `${urlPath}${NAVIGATION_PATHS.resultsDeparture}`;
    return '';
  }

  const goToReservation = () => {
    window.location.href = getManageRedirectionUrl();
  };

  const goToManage = () => {
    const dataLayer = {
      country: globalCountry,
      language: lang,
      currency,
      interaction: `air:manageFlight:clicked`,
    };
    trackInteraction(dataLayer);
    setTimeout(() => {
      navigate(`${urlPath}${NAVIGATION_PATHS.manage}`, { state: { refetch: true } });
    }, 1500);
  };

  const extraDataConfirmation = {
    departureFlight: selectedDeparture,
    returnFlight: selectedReturn,
  };

  return (
    <>
      <Helmet>
        <title>{getDictionaryValue(dict.airConfirmationFlightSeoTitle.key)}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Grid container className={classes.gridContainer}>
        <div className="notifications">
          {(showTAReserveBanner || isGroupBooking) && !isRefund && showSuccessBanner && (
            <div className={styles.notification}>
              <NotificationBannerComponent
                title={getDictionaryValue(dict.airTravelAgentReserveConfirmationTitle.key)}
                description={`${getDictionaryValue(
                  dict.airTravelAgentReserveConfirmationDescription.key,
                )} ${localizedDayjs(bookingProfile?.flightProfile.booking.finalPaymentDate).format(
                  'MMM DD, YYYY',
                )}`}
                type="success"
                renderCloseBtn
                isBannerOpen
                onClose={() => {
                  setShowSuccessBanner(false);
                }}
                data={dataFlight}
                extraData={extraDataConfirmation}
                isBooked={payNowSelected}
                balanceLabel
                isSummary
                summaryBalance={!payNowSelected}
                isChangeFlights={isChangeFlights}
                handleAccordion={() => setExpanded(!expanded)}
                isDesktop={expanded}
                cardTitle={
                  isChangeFlights
                    ? getDictionaryValue(dict.airFlightSummaryNewFlightsLabel.key)
                    : getDictionaryValue(dict.airFlightSummaryTitle.key)
                }
                payNowSelected={payNowSelected}
              />
            </div>
          )}
          {transfersDetails?.length > 0 && showTransferBanner && (
            <div className={styles.notification}>
              <NotificationBannerComponent
                title={getDictionaryValue(dict.airConfirmationPageTransferBannerTitle.key)}
                description={`${getDictionaryValue(
                  dict.airConfirmationPageTransferBannerDescription1.key,
                )} ${transfersDetails?.map((item) => `#${item.transferCode}`)} ${getDictionaryValue(
                  dict.airConfirmationPageTransferBannerDescription2.key,
                )}`}
                type="info"
                renderCloseBtn
                isBannerOpen
                onClose={() => {
                  setShowTransferBanner(false);
                }}
              />
            </div>
          )}
        </div>
        <Grid item xs={12} className={classes.confirmationContainer}>
          <div className={classes.confirmationHeader}>
            <CheckIcon className={classes.checkIcon} />
            <Typography className={classes.confirmationTitle}>
              {getDictionaryValue(dict.airFlightConfirmationPageTitle.key)}
            </Typography>
            <Typography className={classes.confirmationSubtitle}>
              {getDictionaryValue(dict.airTakeTimeWarning.key)}
            </Typography>
          </div>

          <div className={classes.confirmationReviewCard}>
            <ReviewCard
              confirmationNumbers={{
                departing: departingConfirmNumbers,
                returning: returningConfirmNumbers,
              }}
              data={dataFlight}
              extraData={extraDataConfirmation}
              isBooked={payNowSelected}
              balanceLabel
              isSummary
              summaryBalance={!payNowSelected}
              isChangeFlights={isChangeFlights}
              handleAccordion={() => setExpanded(!expanded)}
              isDesktop={expanded}
              cardTitle={
                isChangeFlights
                  ? getDictionaryValue(dict.airFlightSummaryNewFlightsLabel.key)
                  : getDictionaryValue(dict.airFlightSummaryTitle.key)
              }
              payNowSelected={payNowSelected}
              isOneWay={isOneWay}
              confirmation
            />
          </div>
          <div className={classes.confirmationCta}>
            <Button variant="outlined" color="primary" onClick={goToManage}>
              {getDictionaryValue(dict.airConfirmationManageFlightsCTA.key)}
            </Button>
            <Button
              data-testid="go-to-cruise-reservation-btn"
              onClick={goToReservation}
              variant="contained"
              color="primary">
              {getDictionaryValue(dict.airGoToCruiseReservationLabel.key)}
            </Button>
          </div>
          <TravelPlansUnified supportPhoneNumber={airSupportPhoneNumber} />
        </Grid>
      </Grid>
    </>
  );
};

export default Confirmation;
